<template>
  <div>
    <div class="header-search-form d-block">
      <template v-if="multiHeadline">
        <div
          v-if="headerSearchTitleHtml"
          v-dompurify-html="headerSearchTitleHtml"
          class="h1"
        />
        <div
          v-else-if="headerSearchTitle != ''"
          class="h1"
        >
          {{ headerSearchTitle }}
        </div>
        <!-- eslint-disable vue/no-v-html -->
        <h1
          v-if="headerSearchSubtitle !== ''"
          v-dompurify-html="headerSearchSubtitle"
          class="h2 px-2"
        />
      </template>
      <template v-else>
        <headline-component
          v-if="headerSearchTitle || headerSearchSubtitle"
          :header-class="'h2-xs'"
        >
          {{ headerSearchTitle }} {{ headerSearchSubtitle }}
        </headline-component>
      </template>
      <form
        @submit.prevent="handleSubmit"
      >
        <div
          class="row row-no-pad"
        >
          <div :class="`col-md-${inputColWidth}`">
            <div
              class="form-group-reset destination"
            >
              <suggestion
                id="head_suggestion"
                :button-show="true"
                :only-fill-store="onlyFillStore"
              />
            </div>
          </div>

          <div
            class="header-search-picker-wrap"
            :class="`col-md-${inputColWidth}`"
            tabindex="0"
            aria-label="Datepicker"
            @click="focussed"
            @keypress="focussed"
          >
            <header-search-picker
              :only-fill-store="onlyFillStore"
              :class="'header-search-picker'"
            />
          </div>

          <div
            :class="`col-md-${inputColWidth}`"
          >
            <div
              class="form-group"
            >
              <select-persons
                v-model="persons"
                :value="persons"
                :persons-max="28"
                :class="'persons'"
              />
            </div>
          </div>
          <div
            class="submit"
            :class="`col-md-${inputColWidth}`"
          >
            <button
              id="headerSearchSubmit"
              ref="headerSearchSubmit"
              type="submit"
              class="btn btn-primary btn-block"
            >
              <i class="icon"><icon-search /></i>
              <span class="btn-txt">{{ $t('common:button.search') }}</span>
            </button>
          </div>
        </div>
      </form>
    </div>
    <div
      v-show="experimentPopunderVariant && affiliateRedirectAvailable"
      class="px-3"
    >
      <div class="row mt-2">
        <div class="col-12 col-md-6 col-xl-4 offset-md-6 offset-xl-8 px-0">
          <div class="header-search-form affiliate-redirect-checkbox">
            <b-form-checkbox
              v-model="affiliateRedirectCheckboxVal"
              name="affiliate-redirect"
              @change="setAffiliateRedirectCookie"
            >
              {{ $t('search:affiliateRedirect.checkboxTitle') }}
            </b-form-checkbox>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Suggestion from 'components/Suggestion.vue';
import SelectPersons from 'components/SelectPersons.vue';
import HeaderSearchPicker from 'components/HeaderSearchPicker.vue';
import HeadlineComponent from 'components/HeadlineComponent.vue';
import { BFormCheckbox } from 'bootstrap-vue';

import { searchLocationFilterNames } from 'router/guards/queryConfig';
import searchRedirect from 'mixins/composition/search/SearchFunctions';
import { onMounted } from '@vue/composition-api';
import hydrationPreventionSetup from 'mixins/composition/search/HeaderSearchHydrationPrevention';
import StateProviderSetup from 'mixins/composition/search/StateProvider';
import { getReducedCtx } from 'mixins/composition/search/index';
import { setCookie, getCookieVal } from 'mixins/composition/CookieApi';

import config from 'config/appConfig';
import { mapMutations } from 'vuex';
/* ICONS */
// import IconShieldAlt from 'ICON:IconShieldAlt';
import IconSearch from 'ICON:IconSearch';

import { Search } from 'router/routes/all';

const { affiliate } = config;

export default {
  name: 'HeaderSearchForm',
  components: {
    Suggestion,
    SelectPersons,
    HeaderSearchPicker,
    IconSearch,
    HeadlineComponent,
    BFormCheckbox,
  },
  props: {
    // seo content pages have search results. show lower header img and display button "to search results"
    onlyFillStore: {
      type: Boolean,
      default: false,
    },
    headerSearchTitle: {
      type: String,
      default: '',
    },
    headerSearchTitleHtml: {
      type: String,
      default: '',
    },
    headerSearchSubtitle: {
      type: String,
      default: '',
    },
    multiHeadline: {
      type: Boolean,
      default: true,
    },
    inputColWidth: {
      type: Number,
      default: 3,
    },
    headerSearchQuery: { // object with additional query parameters for search submit
      type: Object,
      default: () => {},
    },
  },
  setup(props, ctx) {
    const { isSeoPage, isSeaPage } = StateProviderSetup(getReducedCtx(ctx));
    const { setInitialSearchTerms, shouldSearch } = hydrationPreventionSetup(ctx.root.$store, ctx.parent);
    onMounted(() => {
      setInitialSearchTerms();
    });
    return {
      searchRedirect,
      shouldSearch,
      isSeoPage,
      isSeaPage,
      setCookie,
      getCookieVal,
    };
  },
  i18nOptions: { namespaces: 'search' },
  data() {
    return {
      affiliateRedirectCheckboxVal: true,
    };
  },
  computed: {
    persons: {
      get() {
        return (this.$store.getters.getSearchFilter('persons') || 2).toString();
      },
      set(newPersons) {
        this.$store.dispatch('HYDRATE_SEARCH_OBJECTS', true);
        if (this.onlyFillStore) {
          this.setSearchFilter({
            persons: newPersons,
          });
        } else {
          this.search({ filters: { persons: newPersons.toString() }, router: this.$router });
        }
      },
    },
    isOfferLandingPage() {
      return this.$store.getters.getIsOfferLandingPage;
    },
    experimentPopunderVariant() {
      // AB-Test EDVUE-3811
      const experiment = this.$store.getters.getExperimentById('experiment_EDVUE-3811');
      return experiment?.variant === 'variant';
    },
    // checkbox should only be available on seo and sea pages, not on offer pages
    // checkbox should only be available once per session
    affiliateRedirectAvailable() {
      let returnVal = false;
      // client specific config
      if (affiliate.redirect.showCheckbox) {
        if (!this.experimentPopunderVariant) {
          return returnVal;
        }

        // only sea/seo page which is not offer page
        if ((this.isSeoPage || this.isSeaPage) && !this.isOfferLandingPage) {
          // only if user did not already see checkbox or cookie is too old
          if (!this.checkAffiliateRedirectCookie()) {
            returnVal = true;
          }
        }
      }
      return returnVal;
    }
  },
  mounted() {
    // set checkbox val to false if checkbox is not visible
    this.affiliateRedirectCheckboxVal = this.affiliateRedirectAvailable;
  },
  methods: {
    ...mapMutations({ setSearchFilter: 'SET_SEARCHFILTER' }),
    focussed(event) {
      // eslint-disable-next-line no-console
      console.log('focus fired', event);
      this.$store.dispatch('HYDRATE_SEARCH_OBJECTS', true);
    },
    handleSubmit() {
      // reset all location vars if suggestions was used
      let overrides = {};
      if (Object.keys(this.$store.getters.getMergedTerms).includes('q')) {
        overrides = searchLocationFilterNames.filter((el) => el !== 'q').reduce((aggr, current) => {
          aggr[current] = '';
          return aggr;
        }, {});
      }

      this.setAffiliateRedirectCookie();

      // only search if filters changed
      if (this.shouldSearch() || this.affiliateRedirectCheckboxVal === true) {
        // router push to update URL
        searchRedirect({
          router: this.$router,
          filters: {
            ...overrides,
            ...this.$store.getters.getMergedTerms,
            ...this.headerSearchQuery,
          },
          affiliateRedirect: this.affiliateRedirectCheckboxVal,
        });
      } else if (this.$route.hash !== '#searchResult') {
        this.$router.push({
          ...this.$route,
          hash: '#searchResult',
        });
      }
      return true;
    },
    preloadSearch() {
      // preloading search because we assume user will click search when changing values
      Search();
    },
    // only show affiliate redirect checkbox once per session
    // hide checkbox if user prevoiusly toggled checkbox or submitted search form
    // save timestamp in cookie as value to check later if cookie is too old and can be ignored
    //  - some browsers restore session cookies after reload
    setAffiliateRedirectCookie() {
      if (this.affiliateRedirectAvailable) {
        const cookieValue = new Date();
        setCookie({
          name: 'affiliateRedirectCheckboxHide',
          value: cookieValue,
          expires: '0',
        });
      }
    },
    // check if cookie is set and timestamp value not older than x hours
    // if this is the case, return true
    checkAffiliateRedirectCookie() {
      const cookieValue = getCookieVal('affiliateRedirectCheckboxHide');
      const maxCookieLifetimeHours = 4;
      if (cookieValue) {
        const cookieDate = new Date(cookieValue);
        const nowDate = new Date();
        const dateDifferenceHours = Math.abs(Math.floor((nowDate - cookieDate) / 1000 / 60 / 60));
        return dateDifferenceHours < maxCookieLifetimeHours;
      }
      return false;
    }
  },
};
</script>
<style lang="scss" src="@/scss/components/search/HeaderSearchForm.scss" scoped ></style>
<style lang="scss" src="scss/components/search/HeaderSearchForm.scss" scoped clientextend></style>

<style lang="scss" src="@/clients/wlv/scss/components/search/HeaderSearchFormAdditional.scss" scoped clientextend></style>